import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { getArtworkImageSrc } from '../../utils/artworkImage';
import Navbar from '../Navbar';
import ArtozphereCartBar from './ArtozphereCartBar';

const ArtozphereCart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, getCartTotal, clearCart } = useCart();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="ml-0 md:ml-20 max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-serif">Shopping Cart</h1>
            <ArtozphereCartBar />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-600 mb-4">Your cart is empty</p>
            <Link
              to="/artozphere"
              className="inline-block bg-black text-white px-6 py-3 rounded-full hover:bg-gray-800 transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="ml-0 md:ml-20 max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-serif">Shopping Cart</h1>
          <div className="flex items-center gap-3">
            <ArtozphereCartBar />
            <button
              type="button"
              onClick={clearCart}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
            >
              Clear Cart
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {cartItems.map((item) => {
                const imageSrc = getArtworkImageSrc(item);
                const artistName =
                  typeof item.artist === 'object'
                    ? item.artist?.username ||
                      `${item.artist?.firstName || ''} ${item.artist?.lastName || ''}`.trim()
                    : item.artist;

                return (
                  <div
                    key={item._id}
                    className="p-6 border-b border-gray-200 last:border-b-0"
                  >
                    <div className="flex gap-4">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={item.title}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                          No image
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-medium text-lg">{item.title}</h3>
                        {artistName && <p className="text-gray-600">by {artistName}</p>}
                        <p className="font-semibold mt-1">${item.price}</p>
                        <p className="text-sm text-gray-500 mt-1">Unique artwork</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFromCart(item._id)}
                        className="text-red-500 hover:text-red-600 px-3 py-1 border border-red-300 rounded hover:bg-red-50 transition-colors h-fit"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span>Items ({cartItems.length})</span>
                  <span>${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>Free</span>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>${getCartTotal().toFixed(2)}</span>
                </div>
              </div>
              <button
                type="button"
                className="w-full bg-black text-white py-3 px-6 rounded-full hover:bg-gray-800 transition-colors"
                onClick={() => navigate('/artozphere/checkout')}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtozphereCart;
